/**
 * The frame at the top left of a side of the card: the one chosen on arriving at that side
 * (the owner, 2026-11-04: moving to another side left the last side's frame chosen, and a picture
 * picked next changed on a side out of sight; top right at first, then "i meant top left").
 *
 * "Top left" is measured from the top-left corner of the paper as that side shows it: the frame
 * whose edge comes closest wins. Frames about as close (a corner two frames meet in) go to the one
 * whose middle is closer, then to the upper one. Frames that cannot take a picture (for words or a
 * symbol only) and frames left plain give way to any other on that side.
 */
import { centroid } from './placement.ts';
import type { Pt, TemplateDocument } from './types.ts';

/** How close two distances have to be, mm, to count as the same. */
const SAME_MM = 1;

const toSegment = (q: Pt, a: Pt, b: Pt) => {
  const dx = b[0] - a[0], dy = b[1] - a[1];
  const len = dx * dx + dy * dy;
  const t = len ? Math.max(0, Math.min(1, ((q[0] - a[0]) * dx + (q[1] - a[1]) * dy) / len)) : 0;
  return Math.hypot(q[0] - a[0] - t * dx, q[1] - a[1] - t * dy);
};

const contains = (q: Pt, poly: Pt[]) => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [a, b] = [poly[i], poly[j]];
    if ((a[1] > q[1]) !== (b[1] > q[1]) && q[0] < ((b[0] - a[0]) * (q[1] - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside;
  }
  return inside;
};

/** From a point to an outline: 0 inside it. */
const toOutline = (q: Pt, poly: Pt[]) =>
  contains(q, poly) ? 0 : Math.min(...poly.map((a, i) => toSegment(q, a, poly[(i + 1) % poly.length])));

/**
 * The top-left frame of a view, or - for any other id, the editor's 'sheet' - of the printed
 * sheet's first side. `plain` says which frames are left plain. Null only if nothing shows there.
 */
export function topLeftZone(template: TemplateDocument, viewId: string, plain?: (zone: number) => boolean): number | null {
  const view = template.views.find((v) => v.id === viewId);
  const outlines = new Map<number, Pt[][]>();
  let corner: Pt;
  if (view) {
    for (const pc of view.pieces) outlines.set(pc.zone, [...(outlines.get(pc.zone) ?? []), pc.outline]);
    const paper = view.paper.flat();
    corner = [Math.min(...paper.map((p) => p[0])), Math.min(...paper.map((p) => p[1]))];
  } else {
    for (const z of template.zones) if (z.printFace === 1) outlines.set(z.number, [z.outline, ...z.extraOutlines]);
    corner = [0, 0];
  }

  const frames = [...outlines].map(([zone, polys]) => {
    const near = polys.map((poly) => ({ poly, edge: toOutline(corner, poly) })).reduce((a, b) => (b.edge < a.edge ? b : a));
    const middle = centroid(near.poly);
    return { zone, edge: near.edge, middle: Math.hypot(corner[0] - middle[0], corner[1] - middle[1]), y: middle[1] };
  });
  const byNumber = new Map(template.zones.map((z) => [z.number, z]));
  const forPictures = frames.filter((f) => {
    const z = byNumber.get(f.zone);
    return !z?.textOnly && !z?.symbolOnly && !plain?.(f.zone);
  });
  const pool = forPictures.length ? forPictures : frames;
  if (!pool.length) return null;

  const edge = Math.min(...pool.map((f) => f.edge));
  return pool.filter((f) => f.edge <= edge + SAME_MM)
    .sort((a, b) => (Math.abs(a.middle - b.middle) > SAME_MM ? a.middle - b.middle : a.y - b.y))[0].zone;
}
